/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Content,
  InfoCard,
  Page,
  Progress,
  ResponseErrorPanel,
} from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

// Consent page for Backstage-as-OAuth-provider (PLAT-405). The auth backend's OIDC
// authorize endpoint redirects the browser here (`/oauth2/authorize/:sessionId`) after
// a client (e.g. an MCP connector doing DCR) starts the flow. We load the pending
// authorization session, show the user what's asking for access, and on their decision
// call the backend approve/reject endpoints — which bind the session to the signed-in
// user and return the redirect back to the client. Without this route the SPA 404s and
// the OAuth flow can't complete in a browser. See docs/mcp-oauth.md.

type AuthSession = {
  id: string;
  clientId: string;
  clientName?: string;
  scope?: string;
  redirectUri: string;
};

export const OAuthAuthorizePage = () => {
  const { sessionId = '' } = useParams();
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const identityApi = useApi(identityApiRef);

  const [session, setSession] = useState<AuthSession>();
  const [error, setError] = useState<Error>();
  const [busy, setBusy] = useState(false);

  const authHeaders = useCallback(async (): Promise<Record<string, string>> => {
    const { token } = await identityApi.getCredentials();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [identityApi]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const base = await discoveryApi.getBaseUrl('auth');
        const res = await fetchApi.fetch(
          `${base}/v1/sessions/${encodeURIComponent(sessionId)}`,
          { headers: await authHeaders() },
        );
        if (!res.ok) {
          throw new Error(`Failed to load the authorization request (${res.status})`);
        }
        const data = (await res.json()) as AuthSession;
        if (active) setSession(data);
      } catch (e) {
        if (active) setError(e as Error);
      }
    })();
    return () => {
      active = false;
    };
  }, [discoveryApi, fetchApi, authHeaders, sessionId]);

  const decide = useCallback(
    async (action: 'approve' | 'reject') => {
      setBusy(true);
      setError(undefined);
      try {
        const base = await discoveryApi.getBaseUrl('auth');
        const res = await fetchApi.fetch(
          `${base}/v1/sessions/${encodeURIComponent(sessionId)}/${action}`,
          { method: 'POST', headers: await authHeaders() },
        );
        if (!res.ok) {
          throw new Error(
            res.status === 401 || res.status === 403
              ? 'You must be signed in to Backstage to authorize this request.'
              : `Authorization ${action} failed (${res.status})`,
          );
        }
        const { redirectUrl } = (await res.json()) as { redirectUrl: string };
        // Hand control back to the client (e.g. an MCP loopback callback) with the code.
        window.location.assign(redirectUrl);
      } catch (e) {
        setError(e as Error);
        setBusy(false);
      }
    },
    [discoveryApi, fetchApi, authHeaders, sessionId],
  );

  return (
    <Page themeId="tool">
      <Content>
        <InfoCard title="Authorize access">
          {error && <ResponseErrorPanel error={error} />}
          {!session && !error && <Progress />}
          {session && (
            <Stack spacing={2} alignItems="flex-start">
              <Typography variant="body1">
                <strong>{session.clientName || session.clientId}</strong> is requesting
                access to Backstage on your behalf.
              </Typography>
              {session.scope && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Scope: {session.scope}
                </Typography>
              )}
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                After approval you'll be redirected to {session.redirectUri}
              </Typography>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={busy}
                  onClick={() => decide('approve')}
                >
                  Allow access
                </Button>
                <Button variant="outlined" disabled={busy} onClick={() => decide('reject')}>
                  Deny
                </Button>
              </Stack>
            </Stack>
          )}
        </InfoCard>
      </Content>
    </Page>
  );
};
